import { AppointmentCancellationDialogComponent } from '@agent/core/contact/appointment/appointment-cancellation-dialog.component';
import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  AppointmentCancelReason,
  AppointmentStatus,
  IVisioAppointmentDetailLight,
  IVisioAppointmentGetDto,
  VisioAppointmentDetail,
} from '@commons/models/appointment';
import { BehaviorSubject, catchError, map, Observable, tap, throwError } from 'rxjs';

import {
  AppConf,
  APPOINTMENT_FACADE_PREFIX,
  APPOINTMENT_REMINDER_NOTIFICATION,
  AppointmentDto,
  CancelRemindRequestDto,
  CancelRequestDto,
  CancelRequestRequestDto,
  ChanEnum,
  Channel,
  COMETD_INTERVIEW_COUNT,
  COMETD_VISIO_REMINDER,
  ConfirmRequestDto,
  CSR_FACADE_PREFIX,
  HttpService,
  InterviewDto,
  InviteConfirmRequestDto,
  InviteRefuseRequestDto,
  InviteRequestDto,
  isArray,
  MessagingService,
  PostponeRemindRequestDto,
  ProfileFacade,
  Remind,
  RemindDto,
  Reminder,
  ReminderType,
  RescheduleRequestDto,
  Skill,
  SkillDto,
  TimeSlot,
  ToasterFacade,
  TransferAppointmentRequestDto,
  TranslateService,
  UserDto,
  VisioOpenSessionRequestDto,
} from '@contact/upgrade';

import { VisioService } from '../channel-digit/visio/visio.service';
import { AppointmentReschedulingDialogComponent } from './appointment/appointment-rescheduling-dialog.component';

const TRANSFER_APPOINTMENT = `/${APPOINTMENT_FACADE_PREFIX}/transfer`;
const CONFIRM_APPOINTMENT = `/${APPOINTMENT_FACADE_PREFIX}/confirm`;
const INVITE_APPOINTMENT = `/${APPOINTMENT_FACADE_PREFIX}/invite`;
const REFUSE_APPOINTMENT_INVITATION = `/${APPOINTMENT_FACADE_PREFIX}/invite/refuse`;
const CONFIRM_APPOINTMENT_INVITATION = `/${APPOINTMENT_FACADE_PREFIX}/invite/confirm`;
const RESCHEDULE_APPOINTMENT = `/${APPOINTMENT_FACADE_PREFIX}/reschedule`;
const CANCEL_APPOINTMENT = `/${APPOINTMENT_FACADE_PREFIX}/cancel`;
const CANCEL_REQUEST_APPOINTMENT = `/${APPOINTMENT_FACADE_PREFIX}/cancelrequest`;
const APPOINTMENT_TODAY_UPCOMING_COUNT = `/${APPOINTMENT_FACADE_PREFIX}/today/upcoming/count/`;
const APPOINTMENT_TODAY_UPCOMING_LIST = `/${APPOINTMENT_FACADE_PREFIX}/today/upcoming/list/`;

/**
 * format of params passed to appointment rescheduling popup
 */
export interface ModalParamAppointmentRescheduling {
  appointmentId: string;
  campaignId: string;
  originalTimeSlot: TimeSlot;
  comment: string;
}

/**
 * format of params passed to appointment cancellation popup
 */
export interface ModalParamAppointmentCancellation {
  appointmentId: string;
  status: AppointmentStatus;
}

/**
 * format of params passed to appointment transfer popup
 */
export interface ModalParamAppointmentTransfer {
  appointmentId: string;
}

@Injectable({
  providedIn: 'root',
})
export class AppointmentService {
  private campaignBaseUrl: string;
  private profileId: string;
  public appointmentCounter$ = new BehaviorSubject<number>(0);
  private httpBL: HttpService;

  constructor(
    private readonly appConf: AppConf,
    private readonly messagingService: MessagingService,
    private readonly http: HttpService,
    private readonly profileFacade: ProfileFacade,
    private readonly toasterFacade: ToasterFacade,
    private readonly visioService: VisioService,
    private readonly dialog: MatDialog,
    private readonly translateService: TranslateService
  ) {
    this.httpBL = this.http;
    this.http = this.http.disableApiBLPrefix();
    this.appConf.subscribe(({ campaignBaseUrl }) => (this.campaignBaseUrl = campaignBaseUrl ?? ''));
    this.profileFacade.id$.subscribe(profileId => (this.profileId = profileId));

    // Appointment counter update when campaign status changes
    this.messagingService
      .forRemote()
      .listen$<any>(COMETD_INTERVIEW_COUNT)
      .subscribe(message => this.appointmentCounter$.next(message.total));

    // VISIO REMINDER
    this.messagingService
      .forRemote()
      .listen$<RemindDto>(COMETD_VISIO_REMINDER)
      .subscribe(dto => {
        let reminder: Reminder = {
          type: dto.remindType,
          canPostPone: dto.canPostPone,
          contactFirstname: dto.contactFirstName,
          contactLastname: dto.contactLastName,
          apointmentId: dto.appointmentUuid,
          apointmentType: dto.type,
          appointmentDateInMillis: new Date(dto.appointmentDate).getTime(),
          delayInMinute: dto.delay,
          delayPostponeInMinute: dto.nbMinutesForPostPone,
          flowId: dto.flowUuid,
        };

        // compute duration
        let duration: number | undefined = undefined;
        switch (reminder.type) {
          // 20s
          case ReminderType.FIRST:
            duration = 20 * 1000;
            break;
          // diff between now and appointment date
          case ReminderType.SECOND:
            duration = reminder.appointmentDateInMillis - new Date().getTime();
            break;
          default:
            break;
        }

        // open reminder toast
        this.toasterFacade.add({
          componentName: APPOINTMENT_REMINDER_NOTIFICATION,
          duration,
          data: reminder,
        });
      });
  }

  remindMe(appointmentId: string): Observable<Remind> {
    return this.remindMeOrNot(
      {
        appointmentUuid: appointmentId,
        authorUuid: this.profileId,
      },
      'remindmelater'
    );
  }

  notRemindMe(appointmentId: string): Observable<Remind> {
    return this.remindMeOrNot(
      {
        appointmentUuid: appointmentId,
        authorUuid: this.profileId,
      },
      'notremind'
    );
  }

  join(request: VisioOpenSessionRequestDto): Observable<Object> {
    return this.visioService.openVisioAppointment(request.appointmentId);
  }

  /**
   * The service called for rescheduling an appointment
   * @param request: rest dto for service rescheduling
   */
  reschedule(request: RescheduleRequestDto): Observable<AppointmentDto> {
    return this.http.post<AppointmentDto>(`${this.campaignBaseUrl}${RESCHEDULE_APPOINTMENT}`, request);
  }

  /**
   * The service called for cancelling an appointment or a request of appointment
   * @param appointmentId: the id of the  appointment
   * @param status: the status of the appointment
   */
  cancel(appointmentId: string, status: string): Observable<AppointmentDto> {
    if (status === AppointmentStatus.REQUESTED) {
      let request = { appointmentUuid: appointmentId, reason: AppointmentCancelReason.CANCEL_BY_CSR } as CancelRequestDto;
      return this.http.post<AppointmentDto>(`${this.campaignBaseUrl}${CANCEL_REQUEST_APPOINTMENT}`, request);
    } else {
      let request = { appointmentUuid: appointmentId, reason: AppointmentCancelReason.CANCEL_BY_CSR } as CancelRequestRequestDto;
      return this.http.post<AppointmentDto>(`${this.campaignBaseUrl}${CANCEL_APPOINTMENT}`, request);
    }
  }

  /**
   * The service called for inviting participant(s) for an appointment
   * @param request: rest dto for service rescheduling
   */
  inviteParticipant(inviteRequest: InviteRequestDto): Observable<AppointmentDto> {
    return this.http.post<AppointmentDto>(`${this.campaignBaseUrl}${INVITE_APPOINTMENT}`, inviteRequest).pipe(
      catchError((response: HttpResponse<any>) => {
        if (response.status === 400) {
          return throwError(() => ({
            inviteForbiddenMessage: this.translateService.instant(
              'agent:popin.invite_appointment_participant.already_accepted_error_message'
            ),
          }));
        } else {
          return throwError(() => this.translateService.instant('commons:session.unknow_error'));
        }
      })
    );
  }

  /**
   * The service called for accepting an appointment
   * @param appointmentUuid: the id of the  appointment
   * @param csr: the csr who accept the appointment
   */
  acceptAppointment(appointmentUuid: string, csr: UserDto): Observable<AppointmentDto> {
    return this.http.post<AppointmentDto>(`${this.campaignBaseUrl}${CONFIRM_APPOINTMENT}`, { appointmentUuid, csr } as ConfirmRequestDto);
  }

  /**
   * The service called for accepting an invitation
   * @param request: the request dto to confirm the invitation
   */
  acceptInvitation(request: InviteConfirmRequestDto): Observable<AppointmentDto> {
    return this.http.post<AppointmentDto>(`${this.campaignBaseUrl}${CONFIRM_APPOINTMENT_INVITATION}`, request).pipe(
      catchError((response: HttpResponse<any>) => {
        if (response.status === 409) {
          return throwError(() => ({
            acceptInvitationForbiddenMessage: this.translateService.instant(
              'agent:contact.appointment.visio.accept_invitation_fail_message'
            ),
          }));
        } else {
          return throwError(() => this.translateService.instant('commons:session.unknow_error'));
        }
      })
    );
  }

  /**
   * The service called for refusing an invitation
   * @param request: the request dto to refuse the invitation
   */
  refuseInvitation(request: InviteRefuseRequestDto): Observable<AppointmentDto> {
    return this.http.post<AppointmentDto>(`${this.campaignBaseUrl}${REFUSE_APPOINTMENT_INVITATION}`, request);
  }

  /**
   * Used to open the popup for rescheduling the appointment
   * @param appointmentId: the id of the appointment
   * @param campaignId: the campaign id of the appointment
   * @param originalTimeSlot: the original time slot of the appointment
   */
  openAppointmentReschedulingForm(appointmentId: string, campaignId: string, originalTimeSlot: TimeSlot, comment: string): Observable<any> {
    return this.dialog
      .open(AppointmentReschedulingDialogComponent, {
        data: {
          appointmentId: appointmentId,
          campaignId: campaignId,
          originalTimeSlot: originalTimeSlot,
          comment: comment,
        } as ModalParamAppointmentRescheduling,
      })
      .afterClosed();
  }

  /**
   * Used to open the popup for rescheduling the appointment
   * @param appointmentId: the id of the appointment
   * @param status: the status of the appointment
   */
  openAppointmentCancellationForm(appointmentId: string, status: AppointmentStatus): Observable<any> {
    return this.dialog
      .open(AppointmentCancellationDialogComponent, {
        data: {
          appointmentId: appointmentId,
          status: status,
        } as ModalParamAppointmentCancellation,
      })
      .afterClosed();
  }

  private remindMeOrNot(
    data: PostponeRemindRequestDto | CancelRemindRequestDto,
    action: 'remindmelater' | 'notremind'
  ): Observable<Remind> {
    return this.http.post<Remind>(`${this.campaignBaseUrl}/${APPOINTMENT_FACADE_PREFIX}/${action}`, data);
  }

  getTodayAppointmentNumber() {
    this.http
      .get<number>(`${this.campaignBaseUrl}${APPOINTMENT_TODAY_UPCOMING_COUNT}${this.profileId}`)
      .subscribe(response => this.appointmentCounter$.next(response));
  }

  getTodayAppointments(): Observable<InterviewDto[]> {
    return this.http
      .get<InterviewDto[]>(`${this.campaignBaseUrl}${APPOINTMENT_TODAY_UPCOMING_LIST}${this.profileId}`)
      .pipe(tap(response => this.appointmentCounter$.next(response.length)));
  }

  transferAppointment(appointment: TransferAppointmentRequestDto): Observable<any> {
    return this.http.post(`${this.campaignBaseUrl}${TRANSFER_APPOINTMENT}`, appointment);
  }

  getAppointmentLight(idAppointment: string): Observable<IVisioAppointmentDetailLight> {
    return this.http
      .get<IVisioAppointmentGetDto>(`${this.campaignBaseUrl}/${APPOINTMENT_FACADE_PREFIX}/${idAppointment}`)
      .pipe(map(value => VisioAppointmentDetail.fromDtoToLight(value)));
  }

  getAppointment(idAppointment: string): Observable<AppointmentDto> {
    return this.http.get<AppointmentDto>(`${this.campaignBaseUrl}/${APPOINTMENT_FACADE_PREFIX}/${idAppointment}`);
  }

  private convertSkillDtoToSkill(success): Skill[] {
    if (!!success && isArray(success)) {
      return success
        .map(
          (skillDto: SkillDto): Skill => ({
            id: skillDto.uuid,
            name: skillDto.name,
          })
        )
        .sort((a, b) => {
          const nameA = a.name.toUpperCase();
          const nameB = b.name.toUpperCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
    }
    return [];
  }

  private getSkillsForChannel(_channel: Channel): Observable<Skill[]> {
    return this.httpBL
      .withUrlParams([_channel.type, _channel.direction])
      .post(`${CSR_FACADE_PREFIX}/getSkillsOfDomainForChannel`, undefined)
      .pipe(map((success: any) => this.convertSkillDtoToSkill(success)));
  }

  getSkills(): Observable<Skill[]> {
    return this.getSkillsForChannel(ChanEnum.Out.Phone) as Observable<Skill[]>;
  }

  getVisioSkills(): Observable<Skill[]> {
    return this.getSkillsForChannel(ChanEnum.Out.Visio);
  }
}
